import { FormlyFieldConfig } from '@ngx-formly/core';
import { FormBuilderService } from '../../services/form-builder.service';
import {
  EIremboFormlyFieldSubTypes,
  EIremboFormlyFieldTypes,
  EIremboFormlyFormStateKeys,
  FormField,
} from '@irembo-andela/irembogov3-common';
import { GetEnumToLabelAndValueOptions } from '../form-builder-field-comparator-dropdown-options.utils';
import {
  EFormBuilderDisplayConditionComparator,
  FormBuilderFieldsWithDataSet,
  FormBuilderFieldsWithOptions,
} from '../../models/form-builder-display-comparator.enum';
import {
  IBasicFieldLabelAndDetails,
  IBasicLabelValuePair,
} from '../../models/basic-label-value-pair.model';
import { TFormlyFieldKey } from '../../models/formly-key.type';
import { ServiceComparatorOptions } from '../../constants/comparator-options.constants';
import {
  IBaseConfigurationFormModel,
  ISettings,
} from '../../models/form-builder-types-configurations.model';
import { EServiceChangeRequestComparator } from '../../models/service-change-request-comparator.enum';
import { FETCHED_DATA_FIELD_TYPES } from './form-fields-formly-display/form-fields-formly-display-field-generator.util';

const settingList = [
  {
    label: 'Read Only',
    value: 'readonly',
  },
  {
    label: 'Disabled',
    value: 'disabled',
  },
];

const COMPARATOR_MAP: Record<string, string> = {
  [EServiceChangeRequestComparator.EQ]: '==',
  [EServiceChangeRequestComparator.GT]: '>',
  [EServiceChangeRequestComparator.GTE]: '>=',
  [EServiceChangeRequestComparator.LT]: '<',
  [EServiceChangeRequestComparator.LTE]: '<=',
  [EServiceChangeRequestComparator.NEQ]: '!=',
  [EServiceChangeRequestComparator.CONTAINS]: 'includes',
};

export const getConditionalSettingsForm = (
  formBuilderServiceInstance: FormBuilderService
): FormlyFieldConfig => {
  return {
    key: 'conditionalSettings',
    id: 'conditionalSettings',
    type: EIremboFormlyFieldTypes.block,
    className: 'mt-3 conditionalSettingsPanel',
    props: {
      dragDisabled: true,
      label: 'Configure Conditional Settings',
    },
    fieldGroup: [
      {
        key: 'settings',
        type: EIremboFormlyFieldTypes.customrepeater,
        className: 'form-builder-display-rules',
        props: {
          hideHeader: true,
          required: false,
          addText: 'Add Setting',
          dragDisabled: true,
          template: 'displayRules',
          title: 'Setting',
        },
        fieldArray: {
          key: 'settingsList',
          className: 'form-builder-display-rules-group',
          type: EIremboFormlyFieldTypes['formly-group'],
          props: {
            hideHeader: true,
            placeholder: 'settings List',
            required: false,
            dragDisabled: true,
          },
          fieldGroup: [
            {
              key: 'fieldSetting',
              type: EIremboFormlyFieldTypes.customdropdown,
              props: {
                dragDisabled: true,
                label: 'Field setting(s)',
                placeholder: 'Select a setting',
                hint: 'This setting will be set basing on the configuired condition(s) below.',
                required: true,
                options: [],
                bindLabel: 'label',
                bindValue: 'value',
              },
              expressions: {
                'props.options': buildSettingsOption,
                'props.disabled': disableFirstLevelField,
              },
            },
            {
              key: 'conditions',
              type: EIremboFormlyFieldTypes.customrepeater,
              className: 'form-builder-display-rules',
              props: {
                hideHeader: true,
                required: false,
                addText: 'Add Condition',
                dragDisabled: true,
                template: 'displayRules',
                title: 'Condition',
              },
              fieldArray: {
                key: 'conditionList',
                className: 'form-builder-display-rules-group',
                type: EIremboFormlyFieldTypes['formly-group'],
                props: {
                  hideHeader: true,
                  placeholder: 'Conditions List',
                  required: false,
                  dragDisabled: true,
                },
                fieldGroup: [
                  {
                    key: 'operator',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      hideHeader: true,
                      placeholder: 'AND or OR',
                      required: false,
                      options: GetEnumToLabelAndValueOptions(
                        EFormBuilderDisplayConditionComparator
                      ),
                      bindLabel: 'label',
                      bindValue: 'value',
                    },
                    expressions: {
                      'props.required': (field: FormlyFieldConfig) => {
                        return field?.parent?.key !== '0';
                      },
                      hide: (field: FormlyFieldConfig) => {
                        return field?.parent?.key === '0';
                      },
                    },
                  },
                  {
                    key: 'comparedto',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      label: 'Form Field / Form State',
                      required: true,
                      placeholder: 'Select form field or form state',
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: [
                        {
                          value: 'formField',
                          label: 'Form Field',
                        },
                        {
                          value: 'formState',
                          label: 'Form State',
                        },
                      ],
                    },
                  },
                  {
                    key: 'formField',
                    type: EIremboFormlyFieldTypes.select,
                    props: {
                      label: 'Form field to compare against',
                      required: true,
                      placeholder: 'Select form field to compare against',
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: getCurrentServiceFormFields(
                        formBuilderServiceInstance
                      ),
                      compareWith: (
                        o1: IBasicLabelValuePair<TFormlyFieldKey>,
                        o2: IBasicLabelValuePair<TFormlyFieldKey>
                      ) => {
                        return o1?.value === o2?.value;
                      },
                      change: (field, $event) => {
                        const form = field.form;
                        form?.get('formFieldProperty')?.reset();
                        form?.get('comparator')?.reset();
                        form?.get('fieldvalue')?.reset();
                      },
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        return field?.parent?.model?.comparedto !== 'formField';
                      },
                      'props.required': '!field.hide',
                    },
                  },
                  {
                    key: 'formFieldProperty',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      label: 'Field setting or property to compare against',
                      placeholder:
                        'Select field setting/property to compare against',
                      bindLabel: 'label',
                      bindValue: '',
                      options: [
                        {
                          value: 'fieldvalue',
                          label: 'Field value',
                          type: 'unknown',
                        },
                        {
                          value: 'disabled',
                          label: 'Disabled',
                          type: 'boolean',
                        },
                        {
                          value: 'readonly',
                          label: 'Readonly',
                          type: 'boolean',
                        },
                        {
                          value: 'validity',
                          label: "Field's Validity",
                          type: 'boolean',
                        },
                      ],
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        return field.model?.comparedto !== 'formField';
                      },
                    },
                  },
                  {
                    key: 'formStateValue',
                    type: EIremboFormlyFieldTypes['formly-group'],
                    props: {
                      hideHeader: true,
                      required: false,
                    },
                    fieldGroup: [
                      {
                        key: 'formStateKey',
                        type: EIremboFormlyFieldTypes.select,
                        props: {
                          hideHeader: true,
                          placeholder: 'Select form State Key',
                          required: true,
                          bindValue: 'value',
                          options: [
                            {
                              label: 'Fetched Data',
                              value: EIremboFormlyFormStateKeys.FETCHED_DATA,
                            },
                            {
                              label: 'Application State',
                              value:
                                EIremboFormlyFormStateKeys.APPLICATION_STATE,
                            },
                            {
                              label: 'Applicant Type',
                              value: EIremboFormlyFormStateKeys.APPLICANT_TYPE,
                            },
                            {
                              label: 'Application Locale',
                              value: EIremboFormlyFormStateKeys.LOCALE,
                            },
                          ],
                        },
                        expressions: {
                          'props.description': (field: FormlyFieldConfig) => {
                            let description: string | undefined = undefined;
                            switch (field?.formControl?.value) {
                              case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_ID: {
                                description =
                                  'Stores the business ID for business applicants';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.APPLICANT_BUSINESS_NAME: {
                                description =
                                  'The business name for business applicants';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.APPLICANT_TYPE: {
                                description = 'The applicant type';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.APPLICATION_STATE: {
                                description = 'The application state';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.FETCHED_DATA: {
                                description =
                                  'Fetched data received by fields that fetch external data against thier field key';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.FETCHED_OPTIONS_DATA: {
                                description =
                                  'Fetched list of fetched options received by custom dropdowns with data fetch';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.LOCALE: {
                                description =
                                  'The locale used in the application';
                                break;
                              }
                              case EIremboFormlyFormStateKeys.APPLICANT_USER_ID: {
                                description = 'The applicant user id';
                                break;
                              }
                              default:
                                description = '';
                            }
                            return description;
                          },
                        },
                      },
                      {
                        key: 'fieldKey',
                        type: EIremboFormlyFieldTypes.customdropdown,
                        props: {
                          hideHeader: true,
                          placeholder:
                            'Select the field key in the formState data',
                          required: true,
                          bindLabel: 'label',
                          bindValue: 'value',
                          options: fetchedDataFieldsFormFieldLabelAndDetails(
                            formBuilderServiceInstance
                          ),
                        },
                        expressions: {
                          hide: field => {
                            return !(
                              field?.parent?.parent?.model?.formStateValue
                                ?.formStateKey ===
                              EIremboFormlyFormStateKeys.FETCHED_DATA
                            );
                          },
                        },
                      },
                      {
                        key: 'valuePath',
                        type: EIremboFormlyFieldTypes.input,
                        props: {
                          hideHeader: true,
                          placeholder: 'Value path',
                          required: true,
                          description:
                            'value path to access value e.g `sex` in {sex: "MALE"} or `users?.[0].sex` in  {users: [sex: "MALE"]}',
                        },
                        expressions: {},
                      },
                    ],
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        return field?.parent?.model?.comparedto !== 'formState';
                      },
                      'props.required': '!field.hide',
                    },
                  },
                  {
                    key: 'comparator',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      label: 'Comparator',
                      placeholder: 'Select a comparator',
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: ServiceComparatorOptions,
                    },
                    expressions: {
                      'props.options': buildComparatorOptions,
                      'model.comparator': 'model?.comparator',
                      'props.required': '!field.hide',
                      hide: (field: FormlyFieldConfig) => {
                        if (field?.model?.comparedto == null) return true;

                        if (field?.model?.comparedto === 'formState')
                          return false;

                        return (
                          field?.model?.formFieldProperty?.type === 'boolean' ||
                          ['checkbox'].includes(
                            field?.model?.formField?.fieldType
                          )
                        );
                      },
                    },
                  },
                  {
                    key: 'fieldvalue',
                    type: EIremboFormlyFieldTypes.input,
                    props: {
                      label: 'Field value',
                      required: false,
                      placeholder: 'Enter field value',
                    },
                    validation: {
                      messages: {
                        required: 'This field is required',
                      },
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        if (
                          field.model?.formFieldProperty?.type == 'boolean' ||
                          field.model?.comparator === 'BOOLEAN'
                        ) {
                          return true;
                        }

                        if (field.model?.formField?.value == 'formState') {
                          return false;
                        }

                        return (
                          FormBuilderFieldsWithOptions.includes(
                            field?.model?.formField?.fieldType
                          ) ||
                          FormBuilderFieldsWithDataSet.includes(
                            field?.model?.formField?.fieldType
                          )
                        );
                      },
                      'props.required': '!field.hide',
                    },
                  },
                  {
                    key: 'fieldvalue',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      label: 'Select option',
                      required: false,
                      placeholder: 'Select option',
                      bindLabel: 'label',
                      bindValue: 'value',
                      options: [
                        {
                          value: true,
                          label: 'True',
                        },
                        {
                          value: false,
                          label: 'False',
                        },
                      ],
                    },
                    validation: {
                      messages: {
                        required: 'This field is required',
                      },
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        if (
                          field.model?.formField?.value === 'formState' &&
                          field.model?.comparator !== 'BOOLEAN'
                        ) {
                          return true;
                        }

                        if (
                          field.model?.formFieldProperty?.type === 'boolean' ||
                          ['checkbox'].includes(
                            field?.model?.formField?.fieldType
                          ) ||
                          field.model?.comparator === 'BOOLEAN'
                        ) {
                          return false;
                        }

                        return true;
                      },
                      'props.required': '!field.hide',
                      'props.options': field => {
                        return field.model?.formFieldProperty?.value ===
                          'validity'
                          ? [
                              {
                                value: true,
                                label: 'Is valid',
                              },
                              {
                                value: false,
                                label: 'Not vaild',
                              },
                            ]
                          : [
                              {
                                value: true,
                                label: 'True',
                              },
                              {
                                value: false,
                                label: 'False',
                              },
                            ];
                      },
                    },
                  },
                  {
                    key: 'fieldvalue',
                    type: EIremboFormlyFieldTypes.customdropdown,
                    props: {
                      placeholder: 'Select a value',
                      label: 'Select a value',
                      options: [],
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) => {
                        const { formField } = field?.model || {};
                        const { fieldType } = formField || {};

                        if (
                          field?.model?.formFieldProperty?.type === 'boolean' ||
                          field.model?.formField?.value == 'formState' ||
                          ['checkbox'].includes(
                            field?.model?.formField?.fieldType
                          )
                        ) {
                          return true;
                        }

                        if (
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                        ) {
                          return true;
                        }
                        if (
                          fieldType ===
                            EIremboFormlyFieldTypes.customdropdownpaginated ||
                          [
                            EIremboFormlyFieldTypes.customdropdown,
                            EIremboFormlyFieldTypes.radio,
                            EIremboFormlyFieldTypes.multicheckbox,
                            EIremboFormlyFieldTypes.select,
                          ].includes(fieldType)
                        ) {
                          return false;
                        }

                        return true;
                      },
                      'props.required': '!field?.hide',
                      'props.bindLabel': (field: FormlyFieldConfig) =>
                        formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                          { compareField: field?.model?.formField }
                        )?.props?.['bindLabel'] ?? 'label',

                      'props.bindValue': (field: FormlyFieldConfig) =>
                        formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                          { compareField: field?.model?.formField }
                        )?.props?.['bindValue'] ?? 'value',
                      'props.options': (field: FormlyFieldConfig) => {
                        let options: any = [];
                        if (
                          FormBuilderFieldsWithOptions.includes(
                            field?.model?.formField?.fieldType
                          ) &&
                          field?.model?.formField?.fieldSubType !==
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH &&
                          field?.model?.formField?.fieldSubType !==
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                        ) {
                          options =
                            formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                              { compareField: field?.model?.formField }
                            )?.props?.options;
                          return options;
                        }
                        return options;
                      },
                    },
                  },
                  {
                    key: 'fieldvalue',
                    type: 'input',
                    props: {
                      placeholder: 'Select a value',
                      label: 'Select a value',
                      options: [],
                      useBaseUrl: (field: FormlyFieldConfig) =>
                        field?.model?.formField?.fieldType ===
                          EIremboFormlyFieldTypes.customdropdownpaginated ||
                        field?.model?.formField?.fieldSubType ===
                          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                        field?.model?.formField?.fieldSubType ===
                          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
                      subType: (field: FormlyFieldConfig) =>
                        field?.model?.formField?.fieldSubType,
                    },
                    expressions: {
                      type: (field: FormlyFieldConfig) => {
                        if (
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                        ) {
                          return EIremboFormlyFieldTypes.customdropdown;
                        }
                        if (
                          field?.model?.formField?.fieldType ===
                          EIremboFormlyFieldTypes.customdropdownpaginated
                        ) {
                          return EIremboFormlyFieldTypes.customdropdownpaginated;
                        }
                        return EIremboFormlyFieldTypes.customdropdown;
                      },
                      hide: (field: FormlyFieldConfig) => {
                        if (
                          field?.model?.formFieldProperty?.type === 'boolean' ||
                          field.model?.formField?.value == 'formState' ||
                          ['checkbox'].includes(
                            field?.model?.formField?.fieldType
                          )
                        ) {
                          return true;
                        }

                        if (
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                          field?.model?.formField?.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                        ) {
                          return false;
                        } else {
                          return true;
                        }
                      },
                      'props.required': '!field?.hide',
                      'props.dataset': (field: FormlyFieldConfig) => {
                        const { formField } = field?.model || {};
                        if (!formField) return;

                        if (
                          formField.fieldType ===
                            EIremboFormlyFieldTypes.customdropdownpaginated ||
                          formField.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH ||
                          formField.fieldSubType ===
                            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET
                        ) {
                          return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                            { compareField: field?.model?.formField }
                          )?.props?.['dataset'];
                        }
                      },

                      'props.bindLabel': (field: FormlyFieldConfig) =>
                        formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                          { compareField: field?.model?.formField }
                        )?.props?.['bindLabel'] ?? 'label',

                      'props.bindValue': (field: FormlyFieldConfig) =>
                        formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                          { compareField: field?.model?.formField }
                        )?.props?.['bindValue'] ?? 'value',

                      'props.code': (field: FormlyFieldConfig) => {
                        if (
                          field?.model?.formField?.fieldSubType ===
                          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATAFETCH
                        ) {
                          return formBuilderServiceInstance.getFormFieldOptionLabelAndValue(
                            { compareField: field?.model?.formField }
                          )?.props?.['code'];
                        }
                      },
                    },
                  },
                  {
                    key: 'valueAsNumber',
                    type: EIremboFormlyFieldTypes.checkbox,
                    defaultValue: false,
                    props: {
                      hideHeader: true,
                      label: 'Value as Number',
                    },
                    expressions: {
                      hide: (field: FormlyFieldConfig) =>
                        field?.parent?.model?.formField?.value !==
                          'formState' || field.model?.comparator === 'BOOLEAN',
                    },
                  },
                ],
              },
              expressions: {
                'props.disabled': disableFirstLevelField,
              },
            },
          ],
        },
      },
    ],
    expressions: {},
  };
};

const buildSettingsOption = (field: FormlyFieldConfig) => {
  const settingsModel = field?.parent?.parent?.model;
  return filterSettings(settingsModel, Number(field?.parent?.key));
};

const filterSettings = (
  settingsModel: Record<string, unknown>[] | undefined,
  currentFieldIndex?: number
) => {
  const selectedSettings = settingsModel
    ?.filter((_, index) => index !== currentFieldIndex)
    ?.map((item: any) => item?.fieldSetting);

  const remainingSettings = settingList.filter(
    property => !selectedSettings?.includes(property.value)
  );
  return remainingSettings;
};

export const disableFirstLevelField = (field: FormlyFieldConfig) => {
  const model = field?.parent?.parent?.model;
  const allSettingsAreAdded =
    field?.parent?.key !== '0' && field?.parent?.key !== '1';

  if (model && Array.isArray(model)) {
    return filterSettings(model).length === 0 && allSettingsAreAdded;
  }
  return false;
};

export const getCurrentServiceFormFields = (
  formBuilderServiceInstance: FormBuilderService
) => {
  const formFieldLabelAndDetails = formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(true, true, [
      EIremboFormlyFieldTypes.customcascadingdropdowns,
    ])
    .map(field => {
      return {
        label: field.label,
        value: field,
      };
    });
  return [...formFieldLabelAndDetails];
};

/**
 * Same as getCurrentServiceFormFields but does NOT exclude the currently
 * configured field. Needed for the expression builder where a field's
 * expressions can reference itself (e.g. model?.SURNAME on the SURNAME field).
 */
export const getAllServiceFormFieldsIncludingSelf = (
  formBuilderServiceInstance: FormBuilderService
) => {
  const formFieldLabelAndDetails = formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(true, false, [
      EIremboFormlyFieldTypes.customcascadingdropdowns,
    ])
    .map(field => {
      return {
        label: field.label,
        value: field,
      };
    });
  return [...formFieldLabelAndDetails];
};

export const buildFormlyExpressions = (conditionalSettings: {
  settings: ISettings[];
}): { [key: string]: string } => {
  const result: Record<string, string> = {};

  const OPERATOR_MAP: Record<string, string> = {
    AND: '&&',
    OR: '||',
  };

  if (!conditionalSettings?.settings) {
    return result;
  }

  conditionalSettings.settings.forEach(setting => {
    const expression: string[] = [];
    setting.conditions.forEach(condition => {
      const {
        formField,
        formFieldProperty,
        comparator,
        fieldvalue,
        formStateValue,
        operator,
        valueAsNumber,
        comparedto,
      } = condition;

      if (comparedto === 'formState') {
        if (operator) {
          expression.push(OPERATOR_MAP[operator]);
        }

        let pathList = `formState?.${formStateValue?.formStateKey}`;
        if (formStateValue?.fieldKey) {
          pathList = `${pathList}?.${formStateValue?.fieldKey}`;
        }
        pathList = `${pathList}?.${formStateValue?.valuePath}`;

        let valueToCompare = fieldvalue;
        if (valueAsNumber) {
          valueToCompare = Number(valueToCompare);
        } else {
          valueToCompare = formatComparisonValue(fieldvalue, formField);
        }

        let formattedComparator = '==';
        if (comparator) {
          formattedComparator = COMPARATOR_MAP[comparator] ?? '==';
        }

        expression.push(`${pathList} ${formattedComparator} ${valueToCompare}`);
      }

      if (comparedto === 'formField') {
        const { rootModelPath } = formField;
        let { fieldRefPath } = formField;

        if (
          formField.fieldSubType &&
          [
            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
            EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
          ].includes(formField.fieldSubType as EIremboFormlyFieldSubTypes)
        ) {
          fieldRefPath = `${fieldRefPath}?.value`;
        }

        const fullPath = `${rootModelPath}${
          fieldRefPath ? '?.' + fieldRefPath : ''
        }`;

        const valueToCompare = formatComparisonValue(fieldvalue, formField);

        if (operator) {
          expression.push(OPERATOR_MAP[operator]);
        }

        if (formFieldProperty?.type !== 'boolean') {
          const formattedComparator = comparator
            ? COMPARATOR_MAP[comparator]
            : '==';
          if (comparator === EServiceChangeRequestComparator.CONTAINS) {
            expression.push(
              `${fullPath}.${formattedComparator}(${valueToCompare})`
            );
          } else {
            expression.push(
              `${fullPath} ${formattedComparator} ${valueToCompare}`
            );
          }
        } else {
          const parts = fieldRefPath.split('?.');
          if (formFieldProperty?.value == 'validity') {
            const chain = parts.map((part: any) => `get('${part}')`).join('?.');
            const validity = fieldvalue ? 'valid' : 'invalid';
            const expr = `field?.parent?.parent?.form?.${chain}.${validity}`;
            expression.push(expr);
          } else {
            const chain = parts.reduce((acc, part) => {
              return `${acc}?.fieldGroup?.find(f => f.key === '${part}')`;
            }, 'field?.parent?.parent?.parent');
            const expr = `${chain}?.props?.${formFieldProperty?.value}`;
            expression.push(`${expr} === ${fieldvalue}`);
          }
        }
      }
    });
    const formattedSetting = `props.${setting.fieldSetting}`;
    result[formattedSetting] = expression.join(' ');
  });
  return result;
};

export const formatComparisonValue = (value: any, field: FormField): string => {
  if (typeof value === 'object') {
    if (
      [
        EIremboFormlyFieldTypes.customdropdown,
        EIremboFormlyFieldTypes.customdropdownpaginated,
      ].includes(field.fieldType as EIremboFormlyFieldTypes)
    ) {
      if (
        field.fieldSubType &&
        [
          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET,
          EIremboFormlyFieldSubTypes.CUSTOM_DROPDOWN_DATASET_PAGINATED,
        ].includes(field.fieldSubType as EIremboFormlyFieldSubTypes)
      ) {
        return `'${value.value}'`;
      }
    }

    return JSON.stringify(value);
  }
  if (isNumeric(value)) {
    return value.toString();
  }
  return typeof value === 'string' ? `'${value}'` : value.toString();
};

const isNumeric = (value: any): boolean => {
  if (typeof value === 'number') return true;
  if (typeof value !== 'string') return false;
  return !isNaN(parseFloat(value)) && isFinite(value as any);
};

export const fetchedDataFieldsFormFieldLabelAndDetails = (
  formBuilderServiceInstance: FormBuilderService
) => {
  const configuredField: FormlyFieldConfig | null =
    formBuilderServiceInstance.configureFieldAction$.getValue();

  const fetchedDataFieldsFormFieldLabelAndDetails: IBasicLabelValuePair<string>[] =
    [];

  if (
    FETCHED_DATA_FIELD_TYPES.includes(
      <EIremboFormlyFieldTypes>configuredField?.type
    )
  ) {
    fetchedDataFieldsFormFieldLabelAndDetails.push({
      label: configuredField?.props?.label ?? '',
      value: <string>configuredField?.key ?? '',
    });
  }

  formBuilderServiceInstance
    .getAllFormFieldLabelAndDetails(true, true, [
      EIremboFormlyFieldTypes.customcascadingdropdowns,
    ])
    .forEach((fieldDetails: IBasicFieldLabelAndDetails<TFormlyFieldKey>) => {
      if (
        FETCHED_DATA_FIELD_TYPES.includes(
          <EIremboFormlyFieldTypes>fieldDetails.fieldType
        )
      ) {
        fetchedDataFieldsFormFieldLabelAndDetails.push({
          label: fieldDetails.label,
          value: fieldDetails?.fieldKey ?? '',
        });
      }
    });

  return fetchedDataFieldsFormFieldLabelAndDetails;
};

export const buildComparatorOptions = (field: FormlyFieldConfig) => {
  const options: IBasicLabelValuePair<string>[] = ServiceComparatorOptions;
  const formFieldType: EIremboFormlyFieldTypes =
    field?.model?.formField?.fieldType;

  if (field?.model?.comparedto === 'formState')
    return [
      {
        value: EServiceChangeRequestComparator.GT,
        label: 'Greater Than',
      },
      {
        value: EServiceChangeRequestComparator.LT,
        label: 'Less Than',
      },
      {
        value: EServiceChangeRequestComparator.EQ,
        label: 'Equal To',
      },
      {
        value: 'BOOLEAN',
        label: 'Is Boolean True or False',
      },
    ];

  if (
    FormBuilderFieldsWithOptions.includes(formFieldType) ||
    FormBuilderFieldsWithDataSet.includes(formFieldType) ||
    [
      EIremboFormlyFieldTypes.customidinput,
      EIremboFormlyFieldTypes.customphonenumber,
      EIremboFormlyFieldTypes.textarea,
    ].includes(formFieldType)
  ) {
    const allowedOptions: IBasicLabelValuePair<string>[] = [];
    options.forEach(option => {
      if (
        [
          EServiceChangeRequestComparator.EQ,
          EServiceChangeRequestComparator.NEQ,
        ].includes(option.value as EServiceChangeRequestComparator)
      ) {
        allowedOptions.push(option);
      }
    });
    return allowedOptions;
  } else if (formFieldType === EIremboFormlyFieldTypes.input) {
    const allowedOptions: IBasicLabelValuePair<string>[] = [];
    options.forEach(option => {
      if (
        [
          EServiceChangeRequestComparator.EQ,
          EServiceChangeRequestComparator.NEQ,
          EServiceChangeRequestComparator.GT,
          EServiceChangeRequestComparator.LT,
        ].includes(option.value as EServiceChangeRequestComparator)
      ) {
        allowedOptions.push(option);
      }
    });
    return allowedOptions;
  } else {
    return options;
  }
};

export const hasAtLeastOneCondition = (config: { settings: ISettings[] }) => {
  return (
    Array.isArray(config.settings) &&
    config.settings.some(
      (setting: ISettings) =>
        Array.isArray(setting.conditions) && setting.conditions.length > 0
    )
  );
};

export const updateConditionalSettingFromFormModel = (
  field: FormlyFieldConfig,
  model: IBaseConfigurationFormModel
) => {
  if (
    !model?.conditionalSettings?.settings ||
    model?.conditionalSettings?.settings.length < 1 ||
    hasAtLeastOneCondition(model?.conditionalSettings) === false
  ) {
    delete field?.expressions?.['props.readonly'];
    delete field?.expressions?.['props.disabled'];
    delete field?.props?.['conditionalSettings'];
    if (field?.props) {
      field.props.readonly = model.readonly ?? undefined;
      field.props.disabled = model.disabled ?? undefined;
    }
    return field;
  }

  if (field?.props) {
    field.props['conditionalSettings'] = {
      ...model?.conditionalSettings,
    };
  } else {
    field.props = {
      conditionalSetting: { ...model?.conditionalSettings },
    };
  }

  if (model?.conditionalSettings) {
    field.expressions = {
      ...field.expressions,
      ...buildFormlyExpressions(model.conditionalSettings),
    };
  }
  return field;
};
